<!--
 * Path: packages/frontend/src/core/components/forms/FormBlockWrapper.vue
 * Task: Wrapper for all dynamic form blocks providing a unified header, edit toggle, and strict Flexbox-based alignment.
 * Update: Rebuilt to enforce Gestalt principles. Title and content are tightly grouped and strictly left-aligned, independent of the edit button. Added Save button right next to the title.
-->
<template>
  <div class="form-block-wrapper mb-8">
    <div class="d-flex align-start">
      <!-- Action Buttons (Left Column) -->
      <div v-if="canEdit && !disableEdit" class="d-flex align-center me-3 flex-shrink-0" style="margin-top: -2px; min-height: 32px;">
        <v-btn
          icon
          variant="text"
          size="small"
          :color="isEditing ? 'error' : 'medium-emphasis'"
          class="edit-btn"
          @click="toggleEdit"
        >
          <v-icon size="small">{{ isEditing ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
          <v-tooltip activator="parent" location="top">{{ isEditing ? 'Cancel Edit' : 'Edit' }}</v-tooltip>
        </v-btn>

        <div class="save-action-wrapper" :class="{ 'is-expanded': isEditing }">
          <v-btn
            icon
            variant="tonal"
            size="small"
            color="primary"
            class="ms-1"
            :loading="isSaving"
            @click="$emit('save')"
          >
            <v-icon size="small">mdi-check</v-icon>
            <v-tooltip activator="parent" location="top">Save</v-tooltip>
          </v-btn>
        </div>
      </div>
      <!-- Spacer to keep alignment intact when user is not allowed to edit -->
      <div v-else class="me-3 flex-shrink-0" style="width: 32px; height: 32px;"></div>

      <!-- Right Column: Title and Content sharing the exact same vertical axis -->
      <div class="flex-grow-1 min-w-0">
        <!-- Header -->
        <div class="d-flex align-center mb-1">
          <h3 class="text-h6 font-weight-bold text-high-emphasis" style="line-height: 1.2;">
            {{ title }}
          </h3>

          <v-tooltip v-if="info" location="end" max-width="300">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon="mdi-information-outline" variant="text" size="small" class="ms-1 text-medium-emphasis" @click="$emit('show-info')"></v-btn>
            </template>
            <span>{{ info }}</span>
          </v-tooltip>
        </div>

        <!-- Content Slot -->
        <div class="form-block-content mt-1">
          <slot name="edit" v-if="isEditing"></slot>
          <slot name="read" v-else></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  isEditing?: boolean;
  title: string;
  info?: string;
  canEdit?: boolean;
  disableEdit?: boolean;
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:is-editing', value: boolean): void;
  (e: 'save'): void;
  (e: 'show-info'): void;
}>();

const toggleEdit = () => {
  emit('update:is-editing', !props.isEditing);
};
</script>

<style scoped>
.form-block-wrapper {
  position: relative;
}
.edit-btn {
  opacity: 0.4;
  transition: opacity 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}
.form-block-wrapper:hover .edit-btn {
  opacity: 1;
}
.save-action-wrapper {
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Butterweiches Material Design Easing (Decelerate) für den Layout Shift */
  transition: max-width 0.35s cubic-bezier(0.25, 0.8, 0.5, 1), opacity 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.save-action-wrapper.is-expanded {
  max-width: 44px; /* Platz für den 32px Button + Margin (ms-1) */
  opacity: 1;
}
</style>
